import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, Suspense } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { lesen, schreiben } from "@/lib/speicher";
import { LayoutDashboard, Upload, Users, PhoneOff, BarChart3, LogOut, Copy, BellRing, Euro, GitMerge, Siren, UserCog, UserX, Layers, TrendingUp, PieChart, PanelLeftClose, PanelLeftOpen, Radar, Settings, Link2, ListChecks, X, BookUser, ListTodo, PhoneCall, Eye, Flag, MonitorPlay , Mail, ScrollText, PhoneOutgoing, PackageCheck, Loader2, Search, Bell, Target, Tag, Globe, Inbox, Bot, Headset, Compass, ChevronDown, ChevronRight, Wrench, Swords, ShieldCheck, MapPin, Megaphone, Send, MousePointerClick, Lightbulb, Ban, ListFilter } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Assistent from "@/components/Assistent";
import KundenSuche from "@/components/KundenSuche";
import AufgabeAnlegen from "@/components/AufgabeAnlegen";
import WareBestellen from "@/components/WareBestellen";
import HandyLeiste from "@/components/HandyLeiste";
import BestellenWahl from "@/components/BestellenWahl";
import AppInstallieren from "@/components/AppInstallieren";
import { badgeSetzen } from "@/lib/pwa";

// Vier Abteilungen statt einer langen Liste (Franklin, 10.10. und
// 14.10.2026: "Tagesgeschaeft muss immer das erste sein, das Wallboard
// direkt sichtbar; Vertrieb, Werbung und SEO getrennt; skalierbar, ohne dass
// Mitarbeiter ueberfordert sind"). Jede Abteilung ist ein eigener Arbeits-
// platz mit eigener Farbe und einer Startseite, die sagt, was zu tun ist:
//   Tagesgeschaeft -> Wallboard          Vertrieb -> Vertriebsarbeit
//   Kampagnen      -> Auf einen Blick    Markt    -> Wettbewerb auf einen Blick
// Eine neue Abteilung ist ein Eintrag hier plus Gruppen unten mit ihrem key.
// Datenpflege und Verwaltung gehoeren keiner Abteilung ("system") und stehen
// unten, zugeklappt.
export const ABTEILUNGEN = [
  { key: "service", titel: "Tagesgeschäft", kurz: "Tagesgeschäft", text: "Wallboard, Anrufe, Postfach, Aufgaben, Bestellungen, Kunden",
    icon: Headset, start: "/tafel", punkt: "bg-gradient-to-r from-marke-blau to-marke-blau-hell",
    verlauf: "from-marke-blau to-marke-blau-hell", schatten: "shadow-marke-blau/30",
    aktiv: "bg-marke-blau-tint text-marke-navy", ton: "text-marke-blau-text", tint: "bg-marke-blau-tint" },
  { key: "vertrieb", titel: "Vertrieb", kurz: "Vertrieb", text: "Anrufliste, Frühwarnung, Umsatz, Auswertungen, Agenda",
    icon: Compass, start: "/vertriebsarbeit", punkt: "bg-gradient-to-r from-marke-magenta to-marke-magenta-hell",
    verlauf: "from-marke-magenta to-marke-magenta-hell", schatten: "shadow-marke-magenta/30",
    aktiv: "bg-marke-magenta-tint text-marke-navy", ton: "text-marke-magenta", tint: "bg-marke-magenta-tint" },
  { key: "kampagnen", titel: "E-Mail-Kampagnen", kurz: "Kampagnen", text: "Werbung an Kunden und Leads über Brevo: Listen, Kampagnen, Ergebnisse",
    icon: Megaphone, start: "/werbung", punkt: "bg-gradient-to-r from-amber-500 to-marke-gelb",
    verlauf: "from-amber-500 to-marke-gelb", schatten: "shadow-amber-500/30",
    aktiv: "bg-amber-50 text-marke-navy", ton: "text-amber-700", tint: "bg-amber-50" },
  { key: "markt", titel: "Markt & SEO", kurz: "Markt & SEO", text: "Mitbewerber, Google, KI-Sichtbarkeit, Agenten-Vorschläge",
    icon: Target, start: "/wettbewerb", punkt: "bg-gradient-to-r from-teal-500 to-marke-cyan",
    verlauf: "from-teal-500 to-marke-cyan", schatten: "shadow-teal-500/30",
    aktiv: "bg-teal-50 text-marke-navy", ton: "text-teal-700", tint: "bg-teal-50" },
];

export const NAV_GRUPPEN = [
  // ------------------------------------------------------ Tagesgeschaeft
  // Die Reihenfolge ist der Arbeitstag: zuerst die Tafel (was wartet), dann
  // die Kanaele, in denen Arbeit ankommt, dann die Kunden. Hoechstens sieben
  // Punkte je Gruppe - mehr liest niemand auf einen Blick.
  {
    label: null,
    abteilung: "service",
    items: [
      // Das Wallboard fuer Menschen: eingebettet, mit Menue links, zum
      // Klicken und Ziehen. Der Kiosk im Buero laeuft weiter auf /wallboard.
      { to: "/tafel", icon: MonitorPlay, label: "Wallboard", testid: "nav-wallboard" },
      { to: "/", icon: LayoutDashboard, label: "Überblick", testid: "nav-dashboard", end: true },
    ],
  },
  {
    label: "Tagesarbeit",
    abteilung: "service",
    items: [
      { to: "/telefonate", icon: PhoneCall, label: "Telefonate", testid: "nav-telefonate",
        zaehler: "telefonate" },
      { to: "/postfach", icon: Mail, label: "Kunden-Postfach", testid: "nav-postfach",
        zaehler: "postfach" },
      { to: "/aufgaben-kundenservice", icon: ListTodo, label: "Aufgaben Kundenservice",
        testid: "nav-aufgaben-service", zaehler: "aufgaben_service" },
      // Was wann von wem bestellt wurde - und was noch auf Freigabe wartet.
      { to: "/bestellungen", icon: PackageCheck, label: "Bestellungen", testid: "nav-bestellungen",
        zaehler: "bestellungen" },
      { to: "/unbekannt", icon: PhoneOff, label: "Unbekannte Kontakte", testid: "nav-unknown",
        zaehler: "unbekannte_kontakte" },
      { to: "/telefonbuch", icon: BookUser, label: "Telefonbuch & Leads", testid: "nav-phonebook" },
    ],
  },
  {
    label: "Kunden",
    abteilung: "service",
    items: [
      { to: "/kunden", icon: Users, label: "Kunden", testid: "nav-customers" },
      { to: "/kundenstatus", icon: Flag, label: "Kundenstatus", testid: "nav-kundenstatus" },
    ],
  },
  // ------------------------------------------------------------ Vertrieb
  {
    label: "Vertrieb",
    abteilung: "vertrieb",
    items: [
      { to: "/vertriebsarbeit", icon: PhoneOutgoing, label: "Vertriebsarbeit", testid: "nav-vertriebsarbeit",
        revenue: true, zaehler: "vertriebsarbeit" },
      { to: "/aufgaben-vertrieb", icon: ListTodo, label: "Aufgaben Vertrieb",
        testid: "nav-aufgaben-vertrieb", zaehler: "aufgaben_vertrieb" },
      { to: "/fruehwarnung", icon: Siren, label: "Frühwarnung", testid: "nav-alerts", revenue: true,
        zaehler: "fruehwarnungen" },
      { to: "/abwanderung", icon: Radar, label: "Abwanderungs-Analyse", testid: "nav-churn", revenue: true },
      { to: "/verloren", icon: UserX, label: "Verlorene Umsätze", testid: "nav-lost", revenue: true },
      { to: "/agenda", icon: ListChecks, label: "Agenda 2026", testid: "nav-agenda" },
    ],
  },
  {
    label: "Auswertung",
    abteilung: "vertrieb",
    items: [
      { to: "/umsatz", icon: Euro, label: "Umsatz", testid: "nav-revenue", revenue: true },
      { to: "/produkt-trends", icon: TrendingUp, label: "Produkt-Trends", testid: "nav-product-trends", revenue: true },
      { to: "/produkte", icon: PieChart, label: "Produkt-Statistik", testid: "nav-products", revenue: true },
      { to: "/material", icon: Layers, label: "Material-Statistik", testid: "nav-materials", revenue: true },
      { to: "/analyse", icon: BarChart3, label: "Statistik", testid: "nav-analytics" },
    ],
  },
  // ------------------------------------------------- E-Mail-Kampagnen
  // Werbung ueber Brevo: Listen aus den Kundendaten und HubSpot, Kampagnen
  // je Land und Sprache, Ergebnisse (wer klickt wo), Sperrliste. Sichtbar
  // nur mit Bereich "werbung"; senden nur mit "werbung_senden".
  {
    label: null,
    abteilung: "kampagnen",
    items: [
      { to: "/werbung", icon: Megaphone, label: "Auf einen Blick", testid: "nav-werbung",
        bereich: "werbung", end: true },
      { to: "/werbung/kampagnen", icon: Send, label: "Kampagnen", testid: "nav-werbung-kampagnen", bereich: "werbung" },
      { to: "/werbung/listen", icon: ListFilter, label: "Listen & Kontakte", testid: "nav-werbung-listen", bereich: "werbung" },
      { to: "/werbung/adressliste", icon: MapPin, label: "Adressliste", testid: "nav-werbung-adressliste", bereich: "werbung" },
      { to: "/werbung/ergebnisse", icon: MousePointerClick, label: "Ergebnisse & Klicks", testid: "nav-werbung-ergebnisse",
        bereich: "werbung" },
      { to: "/werbung/erkenntnisse", icon: Lightbulb, label: "Erkenntnisse", testid: "nav-werbung-erkenntnisse",
        bereich: "werbung" },
      { to: "/werbung/sperrliste", icon: Ban, label: "Sperrliste", testid: "nav-werbung-sperrliste", bereich: "werbung" },
      { to: "/werbung/einstellungen", icon: Settings, label: "Versand & Zustellbarkeit", testid: "nav-werbung-einstellungen",
        bereich: "werbung" },
    ],
  },
  // ------------------------------------------------------ Markt & SEO
  // Markt statt eigener Zahlen: Mitbewerber, Google, KI-Antworten und was
  // die Agenten melden. Sichtbar nur mit Bereich "wettbewerb".
  {
    label: null,
    abteilung: "markt",
    items: [
      { to: "/wettbewerb", icon: Target, label: "Auf einen Blick", testid: "nav-wettbewerb",
        bereich: "wettbewerb", end: true },
      { to: "/wettbewerb/preise", icon: Tag, label: "Mitbewerber-Preise", testid: "nav-wettbewerb-preise",
        bereich: "wettbewerb" },
      { to: "/wettbewerb/internet", icon: Globe, label: "SEO & KI-Sichtbarkeit", testid: "nav-wettbewerb-internet",
        bereich: "wettbewerb" },
      { to: "/wettbewerb/herkunft", icon: MapPin, label: "Aufträge je Land", testid: "nav-wettbewerb-herkunft",
        bereich: "wettbewerb" },
      { to: "/wettbewerb/offensive", icon: Swords, label: "Wettbewerbs-Offensive", testid: "nav-wettbewerb-offensive",
        bereich: "wettbewerb" },
      { to: "/wettbewerb/fakten", icon: ShieldCheck, label: "Fakten für die Agenten", testid: "nav-wettbewerb-fakten",
        bereich: "wettbewerb" },
      // Der Zaehler zeigt offene Meldungen der Preis- und Angebots-Waechter.
      { to: "/wettbewerb/freigaben", icon: Inbox, label: "Freigaben", testid: "nav-wettbewerb-freigaben",
        bereich: "wettbewerb", zaehler: "wettbewerb" },
      { to: "/wettbewerb/agenten", icon: Bot, label: "Agenten", testid: "nav-wettbewerb-agenten",
        bereich: "wettbewerb" },
    ],
  },
  // ------------------------------------------------- gemeinsam: System
  {
    label: "Datenpflege",
    abteilung: "system",
    items: [
      { to: "/import", icon: Upload, label: "Datenimport", testid: "nav-import" },
      { to: "/zuordnung", icon: GitMerge, label: "Zuordnungs-Kontrolle", testid: "nav-mapping", revenue: true },
      { to: "/hubspot-zuordnung", icon: Link2, label: "HubSpot-Zuordnung", testid: "nav-hs-mapping", revenue: true },
      { to: "/dubletten", icon: Copy, label: "Mögliche Dubletten", testid: "nav-duplicates" },
    ],
  },
  {
    // Alles, was im Hintergrund arbeitet (n8n): Ampel, Warnungen, Handgriffe.
    label: "Automationen",
    abteilung: "system",
    items: [
      { to: "/agenten", icon: Bot, label: "Agenten & Automationen", testid: "nav-agenten",
        bereich: "agenten", zaehler: "agenten" },
    ],
  },
  {
    label: "Verwaltung",
    abteilung: "system",
    items: [
      { to: "/benutzer", icon: UserCog, label: "Benutzer & Rechte", testid: "nav-users", manage: true },
      { to: "/aktivitaeten", icon: ScrollText, label: "Aktivitäten", testid: "nav-aktivitaeten", manage: true },
      { to: "/einstellungen", icon: Settings, label: "Einstellungen", testid: "nav-settings", revenue: true },
    ],
  },
];

/** Zu welcher Abteilung gehoert eine Adresse? Laengster passender Menuepunkt
 *  gewinnt; Unbekanntes (Kundenakte, Meldungen) gehoert keiner. */
export function abteilungVon(pfad) {
  let treffer = null;
  let laenge = -1;
  for (const g of NAV_GRUPPEN) {
    for (const n of g.items) {
      const passt = n.to === "/" ? pfad === "/" : (pfad === n.to || pfad.startsWith(n.to + "/"));
      if (passt && n.to.length > laenge) { treffer = g.abteilung; laenge = n.to.length; }
    }
  }
  if (!treffer && pfad.startsWith("/kunden/")) return "service";
  if (!treffer && pfad.startsWith("/bestellen")) return "service";
  return treffer;
}

const CRUMB = {
  "/": "Dashboard", "/agenda": "Agenda 2026", "/tafel": "Wallboard", "/kunden": "Kunden", "/umsatz": "Umsatz",
  "/postfach": "Kunden-Postfach",
  "/aktivitaeten": "Aktivitäten",
  "/vertriebsarbeit": "Vertriebsarbeit",
  "/aufgaben-kundenservice": "Aufgaben Kundenservice",
  "/aufgaben-vertrieb": "Aufgaben Vertrieb",
  "/dubletten": "Mögliche Dubletten", "/unbekannt": "Unbekannte Kontakte",
  "/telefonbuch": "Telefonbuch & Leads", "/telefonate": "Telefonate",
  "/telefonate/auswertung": "Aktivität im Kundenservice",
  "/kundenstatus": "Kundenstatus",
  "/analyse": "Statistik", "/import": "Datenimport", "/zuordnung": "Zuordnungs-Kontrolle",
  "/fruehwarnung": "Frühwarnung", "/produkt-trends": "Produkt-Trends", "/benutzer": "Benutzer & Rechte",
  "/material": "Material-Statistik", "/produkte": "Produkt-Statistik",
  "/verloren": "Verlorene Umsätze & Reaktivierung",
  "/abwanderung": "Abwanderungs-Analyse",
  "/einstellungen": "Einstellungen",
  "/meldungen": "Meldungen & App",
  "/wettbewerb": "Wettbewerb", "/wettbewerb/preise": "Mitbewerber-Preise",
  "/wettbewerb/internet": "SEO & KI-Sichtbarkeit", "/wettbewerb/offensive": "Wettbewerbs-Offensive",
  "/wettbewerb/fakten": "Fakten für die Agenten",
  "/wettbewerb/freigaben": "Freigaben",
  "/wettbewerb/agenten": "Agenten",
  "/werbung": "Kampagnen auf einen Blick", "/werbung/kampagnen": "Kampagnen", "/werbung/listen": "Listen & Kontakte",
  "/werbung/ergebnisse": "Ergebnisse & Klicks", "/werbung/erkenntnisse": "Erkenntnisse",
  "/werbung/sperrliste": "Sperrliste", "/werbung/einstellungen": "Versand & Zustellbarkeit",
  "/agenten": "Agenten & Automationen",
};

/**
 * Der rote Kreis mit der Anzahl offener Posten, wie man ihn von ungelesenen
 * Nachrichten kennt.
 *
 * Bei null bleibt er weg: Ein Kreis mit einer Null wäre kein Hinweis, sondern
 * nur ein Fleck, an den man sich gewöhnt. Ab hundert steht "99+", sonst
 * sprengt die Zahl die Zeile.
 */
function Zaehlerkreis({ wert, schmal }) {
  if (!wert) return null;
  const text = wert > 99 ? "99+" : String(wert);

  // Eingeklappt gibt es keine Beschriftung, an die sich der Kreis anlehnen
  // könnte - dann sitzt er oben rechts auf dem Sinnbild.
  if (schmal) {
    return (
      <span
        data-testid="nav-zaehler"
        className="hidden md:flex absolute top-1 right-1 min-w-[16px] h-4 px-1
                   items-center justify-center rounded-full bg-red-600
                   text-[10px] font-semibold text-white tabular leading-none"
      >
        {text}
      </span>
    );
  }

  return (
    <span
      data-testid="nav-zaehler"
      className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center
                 rounded-full bg-red-600 text-[11px] font-semibold text-white
                 tabular leading-none shrink-0"
    >
      {text}
    </span>
  );
}

export default function AppLayout() {
  const { user, logout, canRevenue, canManage, darfBereich,
          darfUmsatz, vorschauOhneUmsatz, setVorschauOhneUmsatz } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Offene Posten je Menüpunkt. Sie werden beim Seitenwechsel neu geholt -
  // wer gerade etwas abgearbeitet hat, sieht die Zahl sofort sinken - und
  // ansonsten jede Minute, damit auch die Arbeit der Kollegen ankommt.
  const [zaehler, setZaehler] = useState({});

  const zaehlerNeuLaden = useCallback(async () => {
    try {
      const r = await api.get("/menue-zaehler");
      setZaehler(r.data || {});
    } catch {
      // Ohne Zahlen ist das Menü immer noch benutzbar; ein Fehlerhinweis
      // wäre hier nur im Weg.
    }
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    const laden = async () => {
      try {
        const r = await api.get("/menue-zaehler");
        if (!abgebrochen) setZaehler(r.data || {});
      } catch { /* siehe oben */ }
    };
    laden();
    const takt = setInterval(laden, 60000);
    return () => { abgebrochen = true; clearInterval(takt); };
  }, [location.pathname]);
  // Die Zahl am App-Icon (installierte App): was auf mich wartet - meine
  // faelligen Aufgaben und Bestellungen, die auf Freigabe warten.
  useEffect(() => {
    badgeSetzen((zaehler.aufgaben_service || 0) + (zaehler.bestellungen || 0));
  }, [zaehler]);
  // Handy: das Blatt "Bestellen" aus der Leiste unten und die Kundensuche
  // als zweite Zeile unter der Kopfzeile.
  const [bestellenOffen, setBestellenOffen] = useState(false);
  const [sucheOffen, setSucheOffen] = useState(false);
  useEffect(() => { setSucheOffen(false); }, [location.pathname]);
  const crumb = CRUMB[location.pathname]
    || (location.pathname.startsWith("/kunden") ? "Kundendetail" : "")
    || (location.pathname.startsWith("/einstellungen") ? "Einstellungen" : "")
    || (location.pathname.startsWith("/wettbewerb") ? "Wettbewerb" : "")
    || (location.pathname.startsWith("/werbung/kampagnen/") ? "Kampagne" : "")
    || (location.pathname.startsWith("/bestellen/implantate") ? "Implantat-Bauteile bestellen" : "")
    || (location.pathname.startsWith("/bestellen/") ? "Ware bestellen" : "")
    || (location.pathname.startsWith("/bestellungen") ? "Bestellungen" : "");
  const [collapsed, setCollapsed] = useState(() => lesen("sidebar_collapsed") === "true");
  // Auf dem Telefon ist die Seitenleiste eine Schublade über dem Inhalt
  const [mobileOffen, setMobileOffen] = useState(false);
  useEffect(() => { schreiben("sidebar_collapsed", collapsed); }, [collapsed]);
  // Navigation schließt die Schublade – sonst steht sie nach jedem Tap noch da
  useEffect(() => { setMobileOffen(false); }, [location.pathname]);

  // Vier Filter: harte Rechte (Umsatz, Verwaltung), freigeschaltete
  // Bereiche und die vom Admin je Benutzer ausgeblendeten Punkte. Leere
  // Gruppen verschwinden ganz.
  const ausgeblendet = new Set(user?.menue_ausgeblendet || []);
  const gruppen = NAV_GRUPPEN
    .map((g) => ({ ...g, items: g.items.filter((n) =>
      (!n.revenue || canRevenue) && (!n.manage || canManage)
      && (!n.bereich || darfBereich(n.bereich))
      && !ausgeblendet.has(n.to)) }))
    .filter((g) => g.items.length > 0);

  // Welche Abteilung ist offen? Die Seite entscheidet: Wer eine Vertriebs-
  // seite oeffnet (auch per Link, Suche oder Assistent), ist im Vertrieb.
  // Seiten ohne Abteilung (Kundenakte, Verwaltung) lassen die Wahl stehen.
  const verfuegbar = ABTEILUNGEN.filter((a) => gruppen.some((g) => g.abteilung === a.key));
  const [abteilungWahl, setAbteilungWahl] = useState(() => lesen("abteilung") || "service");
  const seitenAbteilung = abteilungVon(location.pathname);
  useEffect(() => {
    if (seitenAbteilung && seitenAbteilung !== "system") setAbteilungWahl(seitenAbteilung);
  }, [seitenAbteilung]);
  useEffect(() => { schreiben("abteilung", abteilungWahl); }, [abteilungWahl]);
  const abteilung = verfuegbar.find((a) => a.key === abteilungWahl) || verfuegbar[0] || ABTEILUNGEN[0];
  const abteilungsGruppen = gruppen.filter((g) => g.abteilung === abteilung.key);
  const systemGruppen = gruppen.filter((g) => g.abteilung === "system");
  // Offene Posten je Abteilung - ein Punkt am Umschalter sagt "drueben
  // wartet etwas", ohne dass man hinueberwechseln muss.
  const offenIn = (key) => gruppen.filter((g) => g.abteilung === key)
    .flatMap((g) => g.items).reduce((summe, n) => summe + (n.zaehler ? (zaehler[n.zaehler] || 0) : 0), 0);
  const abteilungWechseln = (key) => {
    if (key === abteilung.key) return;
    setAbteilungWahl(key);
    // Gleich zur Startseite der Abteilung - ein Umschalter, der nur das
    // Menue tauscht und die alte Seite stehen laesst, wirkt kaputt. Die
    // Startseite sagt, was zu tun ist (Wallboard, Anrufliste, Auf einen Blick).
    const ab = ABTEILUNGEN.find((a) => a.key === key);
    const items = gruppen.filter((g) => g.abteilung === key).flatMap((g) => g.items);
    const ziel = items.find((n) => n.to === ab?.start) || items[0];
    if (ziel && abteilungVon(location.pathname) !== key) navigate(ziel.to);
  };
  const [systemOffen, setSystemOffen] = useState(() => lesen("menue_system_offen") === "true");
  useEffect(() => { if (seitenAbteilung === "system") setSystemOffen(true); }, [seitenAbteilung]);
  useEffect(() => { schreiben("menue_system_offen", systemOffen); }, [systemOffen]);

  const schmal = collapsed && !mobileOffen;
  const kopfAbteilung = ABTEILUNGEN.find((a) => a.key === seitenAbteilung) || ABTEILUNGEN[0];

  // Eine Menuegruppe. stil = Abteilung, deren Farbe der aktive Punkt traegt.
  const gruppeZeichnen = (g, gi, stil) => (
            <div key={g.label || gi} className={`${gi > 0 ? "mt-3" : ""} flex flex-col gap-0.5 items-stretch`}>
              {g.label && (
                <div className={`px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400 select-none ${schmal ? "md:hidden" : ""}`}>
                  {g.label}
                </div>
              )}
              {g.label && schmal && <div className="hidden md:block h-px w-8 bg-slate-200 my-1 ml-3" />}
              {g.items.map(({ to, icon: Icon, label, testid, end,
                              zaehler: schluessel, neuesFenster }) => {
                const offen = schluessel ? zaehler[schluessel] : 0;
                // Vollansichten (Wallboard) im neuen Fenster: Wer den Link
                // im selben Tab oeffnete, verlor die Anwendung samt Menue.
                if (neuesFenster) {
                  return (
                    <Tooltip key={to}>
                      <TooltipTrigger asChild>
                        <a href={to} target="_blank" rel="noopener noreferrer"
                           data-testid={testid}
                           className="relative h-11 md:h-9 w-full justify-start px-3 rounded-lg
                                      flex items-center gap-2.5 transition-colors text-slate-800
                                      hover:bg-slate-100">
                          <Icon className="h-[18px] w-[18px] shrink-0 text-slate-500" />
                          <span className={`text-sm font-semibold truncate ${schmal ? "md:hidden" : ""}`}>
                            {label}
                          </span>
                        </a>
                      </TooltipTrigger>
                      {schmal && <TooltipContent side="right">{label}</TooltipContent>}
                    </Tooltip>
                  );
                }
                // Aktiv-Zustand selbst bestimmen statt ueber die
                // className-FUNKTION von NavLink: Der Tooltip-Wrapper
                // (Radix asChild) reicht eine Funktion ungeprueft als
                // rohen Text ans Element durch - im class-Attribut stand
                // woertlich der Quelltext, und weil darin zufaellig
                // "text-white" vorkam, war die Beschriftung weiss auf
                // weiss. Ein schlichter String kann nicht kaputtgehen.
                const aktiv = end
                  ? location.pathname === to
                  : location.pathname === to || location.pathname.startsWith(to + "/");
                return (
                  <Tooltip key={to}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={to}
                        end={end}
                        data-testid={testid}
                        className={`relative h-11 md:h-9 w-full justify-start px-3 rounded-lg flex items-center gap-2.5 transition-colors ${
                          aktiv ? stil.aktiv : "text-slate-800 hover:bg-slate-100"}`}
                      >
                        {/* Aktiv: helle Flaeche in der Abteilungsfarbe und ein
                            schmaler Farbstreifen links - wie auf dem Briefbogen. */}
                        {aktiv && <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-gradient-to-b ${stil.verlauf}`} />}
                        <Icon className={`h-[18px] w-[18px] shrink-0 ${aktiv ? stil.ton : "text-slate-400"}`} />
                        <span className={`text-sm truncate ${aktiv ? "font-bold" : "font-semibold"} ${schmal ? "md:hidden" : ""}`}>{label}</span>
                        <Zaehlerkreis wert={offen} schmal={schmal} />
                      </NavLink>
                    </TooltipTrigger>
                    {schmal && (
                      <TooltipContent side="right">
                        {label}{offen ? ` — ${offen} offen` : ""}
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </div>
  );

  // Vollbild fuer das Wallboard: Der Knopf auf der Tafel schaltet den
  // Browser in den Vollbildmodus; solange das so ist, verschwinden Menue
  // und Kopfzeile, und die Tafel fuellt den Bildschirm wie am Kiosk.
  // Esc beendet es - dann kommt alles zurueck.
  const [vollbild, setVollbild] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const h = () => setVollbild(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const istTafel = location.pathname === "/tafel";
  const ohneRahmen = istTafel && vollbild;
  // Zurueck zum Wallboard: Wer von der Tafel aus eine Aufgabe, ein Gespraech
  // oder eine Mail geoeffnet hat, kommt mit einem Klick zurueck.
  const vonTafel = !istTafel && location.state?.von === "wallboard";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Abdunkelung hinter der mobilen Schublade */}
      {mobileOffen && (
        <div className="fixed inset-0 z-30 bg-black/40 md:hidden" data-testid="sidebar-backdrop"
             onClick={() => setMobileOffen(false)} />
      )}

      {/* Sidebar: mobil als Schublade, ab md fest verankert.
          Nichts wird zentriert - Icons und Beschriftungen stehen immer am
          linken Rand, auch wenn die Leiste eingeklappt ist. */}
      <aside
        data-testid="sidebar"
        className={`${ohneRahmen ? "hidden" : ""} ${schmal ? "md:w-16" : "md:w-72"}
          w-72 items-stretch px-3
          fixed inset-y-0 left-0 z-40 h-screen
          border-r border-slate-200/80 bg-gradient-to-b from-white to-slate-50 flex flex-col pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-4 overflow-y-auto
          transition-transform md:transition-[width] duration-200
          ${mobileOffen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="flex items-center mb-4 justify-between px-1">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Das echte Logo auf Navy - wie Visitenkarte und App-Symbol. */}
            {schmal ? (
              // Eingeklappt nur das Logo-Zeichen (Ausschnitt aus dem App-Symbol).
              <img src="/icons/zeichen-96.png" alt="CADMILL" className="h-9 w-9 rounded-lg bg-marke-navy p-1 shrink-0 select-none"
                   draggable={false} />
            ) : (
              <div className="rounded-xl bg-marke-navy px-3 py-2 min-w-0 leading-tight" data-testid="logo-kopf">
                <img src="/logos/cadmill-logo-hell.png" alt="CADMILL" className="h-5 w-auto select-none" draggable={false} />
                <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-marke-blau-hell">Customer Care</div>
              </div>
            )}
          </div>
          {/* Mobil: Schließen-Kreuz. Desktop: Einklappen. */}
          <button onClick={() => setMobileOffen(false)} className="md:hidden text-slate-400 hover:text-slate-900 p-1"
                  data-testid="sidebar-close" title="Schließen">
            <X className="h-5 w-5" />
          </button>
          {!collapsed && (
            <button onClick={() => setCollapsed(true)} data-testid="sidebar-toggle"
                    className="hidden md:block text-slate-400 hover:text-slate-900 shrink-0" title="Einklappen">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>
        {collapsed && (
          <button onClick={() => setCollapsed(false)} data-testid="sidebar-toggle" title="Ausklappen"
                  className="hidden md:flex mb-2 h-8 w-8 rounded-md items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-900 self-start">
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}
        <TooltipProvider delayDuration={0}>
          {/* Die Abteilungen (2 x 2). Nur wer mehr als eine sieht, bekommt den Umschalter. */}
          {verfuegbar.length > 1 && (schmal ? (
            <div className="hidden md:flex flex-col gap-1 mb-3" data-testid="abteilung-wahl">
              {verfuegbar.map((a) => {
                const an = a.key === abteilung.key;
                const offen = !an && offenIn(a.key) > 0;
                return (
                  <Tooltip key={a.key}>
                    <TooltipTrigger asChild>
                      <button onClick={() => abteilungWechseln(a.key)} data-testid={`abteilung-${a.key}`}
                              aria-pressed={an}
                              className={`relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${
                                an ? `bg-gradient-to-br ${a.verlauf} text-white shadow-md ${a.schatten}` : "text-slate-400 hover:bg-slate-100"}`}>
                        <a.icon className="h-[18px] w-[18px]" />
                        {offen && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{a.titel}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <div className="mb-3">
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-marke-grau p-1 ring-1 ring-inset ring-slate-200/70" role="tablist"
                   data-testid="abteilung-wahl">
                {verfuegbar.map((a) => {
                  const an = a.key === abteilung.key;
                  const offen = !an && offenIn(a.key) > 0;
                  return (
                    <button key={a.key} onClick={() => abteilungWechseln(a.key)} role="tab" aria-selected={an}
                            title={a.text}
                            data-testid={`abteilung-${a.key}`}
                            className={`relative flex items-center justify-center gap-1.5 rounded-lg px-1.5 h-11 md:h-9 text-[12px] font-bold transition-all ${
                              an ? `bg-gradient-to-r ${a.verlauf} text-white shadow-md ${a.schatten}`
                                 : "text-slate-500 hover:text-marke-navy hover:bg-white/70"}`}>
                      {/* Sinnbild nur mit Platz - sonst wird "Tagesgeschaeft" abgeschnitten. */}
                      <a.icon className="h-4 w-4 shrink-0 hidden min-[1800px]:block" />
                      <span className="truncate">{a.kurz}</span>
                      {offen && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-slate-100"
                                      title="Dort wartet etwas" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {abteilungsGruppen.map((g, gi) => gruppeZeichnen(g, gi, abteilung))}
          {/* Datenpflege und Verwaltung: in beiden Abteilungen gleich, unten und
              eingeklappt - gebraucht wird es selten, im Weg sein soll es nie. */}
          {systemGruppen.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-200">
              <button onClick={() => setSystemOffen((o) => !o)} data-testid="menue-system"
                      aria-expanded={systemOffen}
                      className="w-full h-11 md:h-8 px-3 rounded-md flex items-center gap-2.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                <Wrench className="h-[18px] w-[18px] shrink-0" />
                <span className={`text-sm font-semibold flex-1 text-left ${schmal ? "md:hidden" : ""}`}>Verwaltung &amp; Daten</span>
                {/* Zugeklappt sieht man sonst nicht, dass hier etwas wartet (z. B. ein Agent mit Fehler). */}
                {!systemOffen && <Zaehlerkreis wert={systemGruppen.flatMap((g) => g.items)
                  .reduce((s, n) => s + (n.zaehler ? (zaehler[n.zaehler] || 0) : 0), 0)} schmal={schmal} />}
                {!schmal && (systemOffen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
              </button>
              {systemOffen && systemGruppen.map((g, gi) => gruppeZeichnen(g, gi + 1, ABTEILUNGEN[0]))}
            </div>
          )}
        </TooltipProvider>
        {/* Nur auf dem Handy: Wer angemeldet ist, Meldungen, Abmelden. Am
            Rechner steht das in der Kopfzeile. */}
        <div className="md:hidden mt-auto pt-4 border-t border-slate-200 space-y-1" data-testid="handy-menue-fuss">
          <div className="px-3 pb-1 text-xs text-slate-500 truncate">{user?.name || user?.email}</div>
          <NavLink to="/meldungen" data-testid="nav-meldungen"
                   className={`h-11 w-full px-3 rounded-md flex items-center gap-2.5 ${
                     location.pathname === "/meldungen" ? "bg-marke-blau-tint text-marke-navy font-bold" : "text-slate-800 hover:bg-slate-100"}`}>
            <Bell className="h-[18px] w-[18px] shrink-0 opacity-70" />
            <span className="text-sm font-semibold">Meldungen &amp; App</span>
          </NavLink>
          <button onClick={logout} data-testid="handy-abmelden"
                  className="h-11 w-full px-3 rounded-md flex items-center gap-2.5 text-slate-700 hover:bg-white">
            <LogOut className="h-[18px] w-[18px] shrink-0 opacity-70" />
            <span className="text-sm font-semibold">Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main - mit festem Rand fuer die fixierte Leiste */}
      <div className={`flex-1 flex flex-col min-w-0 ${ohneRahmen ? "" : schmal ? "md:ml-16" : "md:ml-72"}`}>
        <header className={`h-14 border-b border-slate-200/70 bg-white/85 backdrop-blur-md flex items-center gap-3 md:gap-6 px-3 md:px-6 sticky top-0 z-10 ${ohneRahmen ? "hidden" : ""}`}>
          {/* Auf dem Handy gibt es keinen Burger mehr: "Mehr" in der Leiste
              unten oeffnet das Menue. Der Seitentitel steht dafuer auch dort. */}
          <div className="text-sm text-slate-500 min-w-0 flex-1 md:flex-none md:shrink-0">
            <span className="flex items-center gap-2 min-w-0">
              {/* Wo bin ich? Die Abteilung steht klein vor dem Seitentitel. */}
              {seitenAbteilung && seitenAbteilung !== "system" && verfuegbar.length > 1 && (
                <span className="hidden md:inline-flex items-center gap-2 shrink-0" data-testid="kopf-abteilung">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${kopfAbteilung.tint} ${kopfAbteilung.ton}`}>
                    <span className={`h-2 w-2 rounded-full ${kopfAbteilung.punkt}`} />
                    {kopfAbteilung.titel}
                  </span>
                  <span className="text-slate-300">/</span>
                </span>
              )}
              <span className="text-marke-navy font-bold font-marke md:text-[15px] truncate block" data-testid="seitentitel">{crumb}</span>
            </span>
          </div>
          {/* Kundensuche direkt neben dem Seitentitel – linksbündig, nicht in
              der Mitte schwebend. mr-auto schiebt den Benutzerbereich nach
              rechts, ohne die Suche mitzuziehen. Auf dem Handy ist sie ein
              Knopf, der eine zweite Zeile aufklappt - sonst bleibt fuer den
              Titel kein Platz. */}
          <div className="hidden md:block w-full max-w-md mr-auto min-w-0">
            <KundenSuche />
          </div>
          <button onClick={() => setSucheOffen((o) => !o)} data-testid="suche-umschalten" aria-label="Kunden suchen"
                  className={`md:hidden h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${
                    sucheOffen ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Search className="h-5 w-5" />
          </button>
          {/* Aufgabe anlegen gehört neben die Suche, nicht in ein Untermenü:
              Sie entsteht meistens während eines Anrufs, und dann zählt jeder
              Klick. Auf dem Telefon nur das Sinnbild, sonst wird die Zeile zu
              eng für die Suche. */}
          <AufgabeAnlegen knopf="symbol" onAngelegt={zaehlerNeuLaden} />
          {/* Ware bestellen steht aus demselben Grund hier: Wer am Regal die
              letzte Packung nimmt, hat das Telefon in der Hand und will nur
              scannen - nicht erst eine Seite suchen. */}
          <div className="hidden md:block"><WareBestellen /></div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-[13px] font-bold text-slate-900 leading-tight">{user?.name || user?.email}</div>
              <div className="text-[11px] text-slate-400 leading-tight">{user?.role === "owner" ? "Geschäftsführung"
                : user?.role === "superadmin" ? "Superadmin"
                : user?.role === "admin" ? "Admin" : "Mitarbeiter"}</div>
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full ring-2 ring-white shadow-sm" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-marke-navy to-marke-blau flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="hidden md:flex h-8 w-8 rounded-md items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              title="Abmelden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        {sucheOffen && (
          <div className="md:hidden sticky top-14 z-10 bg-white border-b border-slate-200 px-3 py-2" data-testid="suche-zeile">
            <KundenSuche />
          </div>
        )}
        {!ohneRahmen && <AppInstallieren />}

        {/* Der Inhalt nutzt die volle Breite des Bildschirms. Vorher war er
            bei 1600 Pixeln zu Ende, und auf den Bueromonitoren blieb rechts
            ein Viertel leer, waehrend Tabellen ihre Spalten abschnitten.
            Gedeckelt bei 1800 Pixeln: Breiter werden Zeilen unlesbar, und die
            Aktion am rechten Rand gehoert mit dem Auge nicht mehr zur Zeile.
            Listen mit Aktionen je Zeile (Bestellungen, Agenda) begrenzen sich
            zusaetzlich selbst auf max-w-6xl. */}
        {/* Die Tafel fuellt den Platz ohne Rand und ohne Breitengrenze - sie ist
            ein Bildschirm, keine Seite. */}
        <main className={istTafel
            ? "flex-1 w-full pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0"
            : "flex-1 p-4 md:p-6 2xl:px-8 max-w-[1800px] w-full pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-6"}>
          {vonTafel && (
            <button onClick={() => navigate("/tafel")} data-testid="zurueck-zur-tafel"
                    className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 rounded-full
                               bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold shadow-lg
                               hover:bg-slate-700 transition-colors">
              <MonitorPlay className="h-4 w-4" /> Zurück zum Wallboard
            </button>
          )}
          {/* Wer die Vorschau eingeschaltet hat, muss das jederzeit sehen –
              und wieder herauskommen. Sonst sucht man am nächsten Tag den
              Umsatz und hält die Anwendung für kaputt. */}
          {/* Die Erinnerung an Angefangenes: Was ich selbst auf "in Bearbeitung"
              gesetzt und seit mehr als der Grenze nicht abgeschlossen habe.
              Steht so lange da, bis es erledigt oder zurueck im Pool ist -
              Vergessen ist bei uns der haeufigste Fehler (Franklin, 23.09.2026). */}
          {zaehler?.haengend?.anzahl > 0 && !istTafel && (
            <div className="mb-4 flex items-center gap-3 flex-wrap rounded-md border border-amber-300
                            bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="haengend-hinweis">
              <BellRing className="h-4 w-4 shrink-0 text-amber-600" />
              <span>
                <b>Du hast {zaehler.haengend.anzahl} {zaehler.haengend.anzahl === 1 ? "Vorgang" : "Vorgänge"} seit mehr als {zaehler.haengend.stunden} Stunden in Bearbeitung</b>
                {zaehler.haengend.anrufe > 0 && <> · {zaehler.haengend.anrufe} {zaehler.haengend.anrufe === 1 ? "Anruf" : "Anrufe"}</>}
                {zaehler.haengend.aufgaben > 0 && <> · {zaehler.haengend.aufgaben} {zaehler.haengend.aufgaben === 1 ? "Aufgabe" : "Aufgaben"}</>}
                {" "}– erledigen oder zurück in den Pool geben.
              </span>
              <span className="ml-auto flex gap-3 text-xs font-medium">
                {zaehler.haengend.anrufe > 0 && <NavLink to="/telefonate" className="underline hover:text-amber-700">Telefonate</NavLink>}
                {zaehler.haengend.aufgaben > 0 && <NavLink to="/aufgaben-kundenservice" className="underline hover:text-amber-700">Aufgaben</NavLink>}
              </span>
            </div>
          )}
          {vorschauOhneUmsatz && darfUmsatz && (
            <div className="mb-4 flex items-center gap-3 flex-wrap rounded-md
                            border border-amber-300 bg-amber-50 px-4 py-2.5"
                 data-testid="vorschau-balken">
              <Eye className="h-4 w-4 text-amber-700 shrink-0" />
              <div className="text-sm text-amber-900 flex-1 min-w-[16rem]">
                <b>Vorschau:</b> So sieht die Anwendung für Mitarbeiter ohne
                Umsatzrechte aus. Statt Beträgen stehen Einstufungen in Worten.
              </div>
              <button onClick={() => setVorschauOhneUmsatz(false)}
                      data-testid="vorschau-beenden"
                      className="rounded-md border border-amber-400 bg-white px-3 py-1
                                 text-sm font-medium text-amber-900 hover:bg-amber-100">
                Vorschau beenden
              </button>
            </div>
          )}
          {/* Waehrend ein Seitenpaket nachgeladen wird, bleiben Menue und
              Kopfzeile stehen; nur der Inhalt zeigt kurz den Kreisel. */}
          <Suspense fallback={<div className="flex items-center justify-center py-16" data-testid="seite-laedt"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Handy: die Leiste unten (Wallboard, Aufgaben, Bestellen, Bestellungen,
          Mehr) und das Blatt mit den Lieferanten. Am Rechner gibt es beides
          nicht - dort steht das Menue links. */}
      <HandyLeiste zaehler={zaehler} verborgen={ohneRahmen}
                   onBestellen={() => setBestellenOffen(true)} onMehr={() => setMobileOffen(true)} />
      <BestellenWahl offen={bestellenOffen} onOffen={setBestellenOffen} />

      {/* In jeder Ansicht erreichbar - er kennt die aktuelle Seite und
          kann von dort aus weiterhelfen. */}
      <Assistent />
    </div>
  );
}
